import { Suspense, type Ref } from 'react';
import { Skeleton } from '@mui/material';
import { lazyWithChunkReload } from 'src/helpers/chunkLoadError';
import type { RichTextEditorRef } from './RichTextEditorImpl';
import type { RichTextEditorProps } from './types/index';

const loadEditor = () => import('./RichTextEditorImpl');

// Quill (+ lodash-es, parchment, quill-blot-formatter-2) is only needed by the
// ~20 screens that actually embed a rich text editor. Lazy-loading it here
// keeps it out of every page's eager bundle without touching any call site.
const LazyRichTextEditor = lazyWithChunkReload(loadEditor);

/** Fetch the editor chunk ahead of the first Edit click. */
export const preloadRichTextEditor = () => {
  loadEditor().catch(() => {});
};

// Its own boundary: without one, the first editor to mount suspends up to the
// app-level Suspense and the whole page swaps for the loader, like a reload.
const RichTextEditor = ({
  ref,
  ...props
}: Omit<RichTextEditorProps, 'ref'> & { ref?: Ref<RichTextEditorRef> }) => (
  <Suspense
    fallback={<Skeleton variant="rounded" height={96} animation="wave" />}
  >
    <LazyRichTextEditor ref={ref} {...props} />
  </Suspense>
);

export default RichTextEditor;
export type { RichTextEditorRef } from './RichTextEditorImpl';
